import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from, catchError, firstValueFrom, tap } from 'rxjs';
import { DataStorageService } from './data.service';

@Injectable({
    providedIn: 'root'
})
export class OfflineCacheService {
    private _http = inject(HttpClient);
    private _storage = inject(DataStorageService);
    private _storeName = 'static-assets';
    private _imagesStoreName = 'static-images';

    /**
     * Get data from network and cache it, or fallback to cache if offline/failed
     * @param url The URL of the asset to fetch
     * @returns Observable of the data
     */
    getStaticData<T>(url: string): Observable<T> {
        const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
        if (navigator.onLine) {
            return this._http.get<T>(normalizedUrl).pipe(
                tap(data => {
                    if (data) {
                        this._storage.setItem(normalizedUrl, data, this._storeName, true);
                    }
                }),
                catchError((error) => {
                    console.error(`Network fetch failed for ${normalizedUrl}, falling back to cache.`, error);
                    return from(this._storage.getItem(normalizedUrl, this._storeName));
                })
            );
        } else {
            // Force return from cache when offline
            return from(this._storage.getItem(normalizedUrl, this._storeName));
        }
    }

    /**
     * Get an image as a base64 data URL, caching it for offline use.
     * Returns the data URL string that can be used in CSS url() or img src.
     * @param url The URL of the image to fetch
     * @returns Promise resolving to a base64 data URL string
     */
    async getImageAsDataUrl(url: string): Promise<string> {
        const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
        // Try cache first
        const cached = await this._storage.getItem(normalizedUrl, this._imagesStoreName);
        if (cached) {
            return cached as string;
        }

        // Fetch from network and convert to base64
        if (navigator.onLine) {
            try {
                const blob = await firstValueFrom(this._http.get(normalizedUrl, { responseType: 'blob' }));
                const dataUrl = await this._blobToDataUrl(blob);
                await this._storage.setItem(normalizedUrl, dataUrl, this._imagesStoreName, true);
                return dataUrl;
            } catch (error) {
                console.warn(`Failed to fetch image ${normalizedUrl}:`, error);
            }
        }

        // Fallback to original URL if nothing cached and offline
        return normalizedUrl;
    }

    /**
     * Prefetch and cache a list of URLs when online.
     * JSON URLs are cached in IndexedDB via DataStorageService.
     * Image URLs are cached as base64 data URLs for offline CSS url() usage.
     * @param urls Array of URLs to prefetch
     */
    async prefetch(urls: string[]): Promise<void> {
        if (!navigator.onLine) {
            return;
        }

        const jsonUrls: string[] = [];
        const imageUrls: string[] = [];

        for (const rawUrl of urls) {
            const url = rawUrl.startsWith('/') || rawUrl.startsWith('./') ? rawUrl : `/${rawUrl}`;
            if (url.endsWith('.json')) {
                jsonUrls.push(url);
            } else {
                imageUrls.push(url);
            }
        }

        // Prefetch JSON data into IndexedDB
        for (const url of jsonUrls) {
            try {
                const data = await firstValueFrom(this._http.get(url));
                if (data) {
                    await this._storage.setItem(url, data, this._storeName, true);
                }
            } catch (error) {
                console.warn(`Failed to prefetch ${url}:`, error);
            }
        }

        // Prefetch images as base64 data URLs into IndexedDB
        for (const url of imageUrls) {
            try {
                await this.getImageAsDataUrl(url);
            } catch (error) {
                console.warn(`Failed to prefetch image ${url}:`, error);
            }
        }
    }

    /**
     * Check if an asset is cached
     * @param url The URL to check
     */
    async isCached(url: string): Promise<boolean> {
        const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
        if (normalizedUrl.endsWith('.json')) {
            const data = await this._storage.getItem(normalizedUrl, this._storeName);
            return !!data;
        }

        const data = await this._storage.getItem(normalizedUrl, this._imagesStoreName);
        return !!data;
    }

    private _blobToDataUrl(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }
}
